import { Flex, View } from "@adobe/react-spectrum";
import { Paperclip } from "react-bootstrap-icons";
import moment from "moment";
import {
  truncateText,
  getSenderEmail,
  getSenderName,
  isOutgoingMessageForIcon,
} from "../../../../../utils/helperUtil";
import {
  DocumentIcon,
  InvoiceIcon,
  MailInCommingIcon,
  MailOutCommingIcon,
  MassmailGreen,
  NoticeIcon,
  PhoneInComingIcon,
  PhoneInOutGoingIcon,
  PhoneMissedIcon,
  TaskIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import styles from "./card_component_large.module.css";
import { BulkSelectCheckboxSlot } from "../../../../../components/protected/layout-components/BulkSelectCheckbox";

interface ICardComponentLargeProps {
  item: any;
  hideSenderEmail?: boolean;
  showUnresolvedReferenceIndicator?: boolean;
}

const CardComponentLarge: React.FC<ICardComponentLargeProps> = ({
  item,
  hideSenderEmail = false,
  showUnresolvedReferenceIndicator = false,
}) => {
  const rowId = String(item?.id ?? "");
  const showFollowReferenceBadge =
    showUnresolvedReferenceIndicator &&
    String(item?.followReference ?? "") === "0";
  const formatRecipients = (recipients: any): string => {
    const formatContent = (label: string, data: any, prop: any): string => {
      if (Array.isArray(data)) {
        return `${label}: ${data.map((item) => item[prop]).join(", ")}`;
      } else if (data && typeof data === "object") {
        return `${label}: ${data[prop]}`;
      }
      return "";
    };

    const formatFirst = (label: string, data: any, props: string[]): string => {
      for (const p of props) {
        const s = formatContent(label, data, p);
        if (s) return s;
      }
      return "";
    };

    const toContent = formatContent("To", recipients?.TO, "CONTENT");
    const ccContent = formatContent("Cc", recipients?.CC, "CONTENT");
    const bccContent = formatContent("Bcc", recipients?.BCC, "CONTENT");
    const userContent = formatFirst("Notify", recipients?.USER, [
      "contact",
      "contactName",
      "CONTENT",
      "userName",
      "name",
      "id",
    ]);

    return [toContent, ccContent, bccContent, userContent]
      .filter(Boolean)
      .join(" | ");
  };

  const emailTypeImg = () => {
    const renderIcon = (icon: React.ReactNode) => (
      <View UNSAFE_className={styles.email_type_img_mobile}>
        {icon}
        {showFollowReferenceBadge && (
          <span className={styles.follow_reference_badge}>?</span>
        )}
      </View>
    );

    switch (item.type) {
      case "1":
      case "12":
        return renderIcon(<NoticeIcon size={24} />);
      case "2":
      case "3":
      case "7":
        if (isOutgoingMessageForIcon(item)) {
          return renderIcon(<MailOutCommingIcon size={24} />);
        } else {
          return renderIcon(<MailInCommingIcon size={24} />);
        }
      case "9":
      case "14":
      case "15":
      case "11":
        return renderIcon(<MailOutCommingIcon size={24} />);
      case "4":
      case "5":
        return renderIcon(<DocumentIcon size={24} />);
      case "6":
        if (item.status === "131072") {
          return renderIcon(<PhoneInComingIcon size={24} height={16} />);
        } else if (item.status === "262144") {
          return renderIcon(<PhoneInOutGoingIcon size={24} height={16} />);
        } else {
          return renderIcon(<PhoneMissedIcon size={24} height={16} />);
        }
      // return (
      //   <View UNSAFE_className={styles.email_type_img}>
      //     <PhoneInComingIcon size={24} />
      //   </View>
      // );
      case "8":
        return renderIcon(<MassmailGreen size={24} />);
      case "10":
        return renderIcon(<TaskIcon size={24} />);
      case "13":
        return renderIcon(<InvoiceIcon size={24} />);
    }
  };

  return (
    <View width={"100%"} position={"absolute"} top={"0px"} height={"82px"}>
      <Flex
        direction={"row"}
        position={"absolute"}
        top={"12px"}
        left={"50px"}
        alignItems={"center"}
        justifyContent={"start"}
        gap={"8px"}
        width={"calc(100% - 90px)"}
        UNSAFE_className={styles.user_detail_parent_mobile}
      >
        <View
          position={"relative"}
          UNSAFE_className={
            item?.seen == "1"
              ? styles.user_name_read_mobile
              : styles.user_name_unread_mobile
          }
        >
          <span title={item?.sender}>
            {truncateText(getSenderName(item?.sender), 30)}
          </span>
        </View>
        {getSenderEmail(item?.mailFrom) && (
          <View
            width={"1px"}
            position={"relative"}
            height={"12px"}
            UNSAFE_className={styles.user_detail_parent_line_mobile}
          ></View>
        )}
        <View position={"relative"} UNSAFE_className={styles.user_email_mobile}>
          <span title={getSenderEmail(item?.mailFrom)}>
            {truncateText(getSenderEmail(item?.mailFrom), 30)}
          </span>
        </View>
      </Flex>
      <View
        position={"absolute"}
        top={"33px"}
        left={"50px"}
        width={"calc(100% - 170px)"}
        UNSAFE_className={
          item?.seen == "1"
            ? styles.email_subject_read_mobile
            : styles.email_subject_unread_mobile
        }
      >
        <span title={item?.subject}>{truncateText(item?.subject, 36)}</span>
      </View>
      <View
        position={"absolute"}
        top={"33px"}
        right={"8px"}
        width={"150px"}
        UNSAFE_className={styles.email_recipient_large}
      >
        <span title={formatRecipients(item?.RECIPIENT)}>
          {truncateText(formatRecipients(item?.RECIPIENT), 24)}
        </span>
      </View>
      <View
        position={"absolute"}
        bottom={"10px"}
        left={"50px"}
        width={"120px"}
        UNSAFE_className={styles.email_date_mobile}
      >
        {moment(item?.date).format("DD.MM.YYYY HH:mm")}
      </View>
      <View
        position={"absolute"}
        bottom={"12px"}
        right={"16px"}
        width={"20px"}
        UNSAFE_className={styles.email_date_mobile}
      >
        {item?.numberOfAttachments > 0 && <Paperclip size={12} />}
      </View>
      {emailTypeImg()}
      <View
        position={"absolute"}
        top={"10px"}
        right={"10px"}
        UNSAFE_style={{ zIndex: 2 }}
      >
        <BulkSelectCheckboxSlot rowId={rowId} />
      </View>
    </View>
  );
};

export default CardComponentLarge;
